import {
  HandHeart,
  Users,
  Megaphone,
  Trophy,
  Sparkles,
  CheckCircle2,
  Clock,
  HeartHandshake,
} from "lucide-react";

const volunteerImg =
  "https://images.pexels.com/photos/31932515/pexels-photo-31932515.jpeg";

const roles = [
  {
    icon: Users,
    title: "Helpers",
    text: "Set up gear, run the sign-in table, hand out water, and keep the day flowing.",
    commitment: "1–2 hours per week",
  },
  {
    icon: HandHeart,
    title: "Supporters",
    text: "Cheer players on, help pass the ball, and celebrate every basket during practices.",
    commitment: "1 hour per week",
  },
  {
    icon: Trophy,
    title: "Coaches",
    text: "Lead drills and scrimmages with warmth and patience. Basketball experience helpful.",
    commitment: "2 hours per week",
  },
  {
    icon: Megaphone,
    title: "Referees",
    text: "Officiate scrimmages with a friendly whistle and a focus on fun over the rulebook.",
    commitment: "1 hour per week",
  },
];

export default function Volunteer() {
  return (
    <div data-testid="volunteer-page">
      <section className="basketball-bg-accent">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-16 pb-10 md:pt-24 md:pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-700 text-sm font-extrabold uppercase tracking-wider">
              <Sparkles className="w-4 h-4" />
              Join the Team
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 leading-[1.05]">
              Volunteer with <span className="text-orange-500">UHL.</span>
            </h1>
            <p className="mt-5 text-lg font-medium leading-relaxed text-slate-600 max-w-xl">
              UHL volunteers are high school students from our school club
              who are passionate about helping and creating a positive
              environment. No experience required — just a positive
              attitude and willingness to help.
            </p>

            <div className="mt-6 grid grid-cols-2 gap-4 max-w-md">
              {[
                ["High school", "Welcome"],
                ["No experience", "Needed"],
              ].map(([a, b]) => (
                <div
                  key={a}
                  className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm"
                >
                  <p className="font-black text-blue-600">{a}</p>
                  <p className="text-sm text-slate-500 font-semibold">{b}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-xl shadow-blue-900/10 border-[6px] border-white">
            <img
              src={volunteerImg}
              alt="Outdoor basketball hoop"
              className="w-full h-[420px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* ROLES */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-orange-500">
              Volunteer Roles
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              Pick the role that fits you.
            </h2>
            <p className="mt-5 text-slate-600 font-medium text-lg">
              We'll walk you through everything you need to know — just
              bring kindness and comfortable shoes.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {roles.map(({ icon: Icon, title, text, commitment }) => (
              <div
                key={title}
                data-testid={`role-${title.toLowerCase()}`}
                className="bg-[#FAFAFA] rounded-3xl p-8 border border-slate-100 hover:shadow-xl hover:shadow-blue-900/5 hover:-translate-y-1 transition-all flex gap-5"
              >
                <div className="w-14 h-14 flex-shrink-0 rounded-2xl bg-blue-100 flex items-center justify-center">
                  <Icon className="w-7 h-7 text-blue-600" />
                </div>
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="text-xl font-black text-slate-900">{title}</h3>
                    <span className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-wider text-orange-600 bg-orange-100 px-3 py-1 rounded-full">
                      <Clock className="w-3 h-3" /> {commitment}
                    </span>
                  </div>
                  <p className="mt-2 text-slate-600 font-medium leading-relaxed">
                    {text}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-blue-600 rounded-3xl p-8 md:p-10 text-white grid md:grid-cols-3 gap-6">
            {[
              { icon: HeartHandshake, title: "We walk you through it", text: "Quick orientation before your first shift." },
              { icon: CheckCircle2, title: "Flexible commitment", text: "Volunteer weekly, monthly, or just once." },
              { icon: Sparkles, title: "Community hours", text: "Great for service learning & résumés." },
            ].map(({ icon: Icon, title, text }) => (
              <div key={title} className="flex items-start gap-4">
                <Icon className="w-7 h-7 text-orange-300 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-black">{title}</p>
                  <p className="text-blue-100 font-medium">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GOOGLE FORM EMBED PLACEHOLDER */}
      <section className="pb-20">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <div
            data-testid="volunteer-form-embed"
            className="relative rounded-3xl bg-white border border-slate-100 shadow-xl shadow-blue-900/5 p-10 md:p-14 text-center"
          >
            <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center mx-auto">
              <HandHeart className="w-8 h-8 text-orange-500" />
            </div>
            <h3 className="mt-6 text-2xl sm:text-3xl font-black text-slate-900">
              We're getting ready to launch
            </h3>
            <p className="mt-3 text-slate-600 font-medium max-w-xl mx-auto">
              Reach out below to volunteer early or learn more — we'd love
              to hear from you. We'll follow up by email with next steps
              and details.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="mailto:unifiedhoopsleague@gmail.com?subject=Volunteer%20Early%20%E2%80%94%20UHL&body=Hi%20UHL%2C%20I%27d%20like%20to%20volunteer.%20Please%20share%20more%20about%20what%27s%20involved%20and%20how%20I%20can%20help.%0A%0AName%3A%20%0ASchool%20%26%20grade%3A%20%0AAvailability%3A%20%0AAny%20questions%3A%20"
                data-testid="volunteer-early-btn"
                className="inline-flex items-center justify-center gap-2 bg-orange-500 text-white hover:bg-orange-600 hover:shadow-lg hover:-translate-y-0.5 transition-all font-bold px-7 py-3.5 rounded-full"
              >
                📩 Volunteer Early
              </a>
              <a
                href="mailto:unifiedhoopsleague@gmail.com?subject=Volunteer%20Interest%20List%20%E2%80%94%20UHL&body=Hi%20UHL%2C%20please%20add%20me%20to%20the%20volunteer%20interest%20list.%0A%0AName%3A%20%0ASchool%20%26%20grade%3A%20"
                data-testid="volunteer-interest-btn"
                className="inline-flex items-center justify-center gap-2 border-2 border-blue-600 text-blue-600 hover:bg-blue-50 transition-all font-bold px-7 py-3.5 rounded-full"
              >
                📩 Join Interest List
              </a>
            </div>

            <p className="mt-6 text-sm text-slate-500 font-semibold">
              Or write us anytime at{" "}
              <a
                href="mailto:unifiedhoopsleague@gmail.com"
                className="text-orange-600 underline font-bold"
                data-testid="volunteer-email-fallback"
              >
                unifiedhoopsleague@gmail.com
              </a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
